import type { ApiClient } from '../../../shared/api/api-client';

export type FeedbackJournalItem = {
  id: number;
  created_at: string;
  title?: string;
  ticket_kind?: string;
  organization_id?: number | null;
  organization_name?: string | null;
  recipient: string;
  category: string;
  body_md: string;
  message?: string;
  is_anonymous: boolean;
  author_user_id?: number | null;
  /** Имя автора из `users.full_name` (GET list). */
  author_full_name?: string | null;
  /** E-mail автора, если ФИО пустое. */
  author_email?: string | null;
  platform_delivery_queued?: boolean;
  telegram_sent_sync?: boolean;
};

export type FeedbackJournalListResponse = {
  items: FeedbackJournalItem[];
  total: number;
  counters?: { no_reply: number; new_org_comment: number };
};

export type FeedbackJournalListParams = {
  limit?: number;
  offset?: number;
  /** Для GET `/admin/feedback` (tenant / СА с имперсонацией). */
  organization_id?: number | null;
  category?: 'improvement' | 'complaint' | 'freeform' | 'main_admin_change';
  /** Только при явном отборе; не передавать для «все». */
  is_anonymous?: boolean;
  created_from?: string;
  created_to?: string;
};

/** GET `/superadmin/feedback`: пагинация, фильтр адресата и режим инбокса платформы. */
export type SuperadminFeedbackJournalParams = {
  limit?: number;
  offset?: number;
  recipient?: 'developers' | 'org_admin';
  /** true — все обращения к платформе по всем организациям (только суперадмин); иначе только строки текущего пользователя. */
  platformInbox?: boolean;
  /** Кросс-орг отбор (только с GET `/superadmin/feedback`). */
  organization_id?: number;
  category?: 'bug' | 'improvement' | 'complaint' | 'freeform' | 'main_admin_change';
  is_anonymous?: boolean;
  created_from?: string;
  created_to?: string;
  status?: 'open' | 'in_progress' | 'resolved' | 'rejected';
  no_reply?: boolean;
  new_org_comment?: boolean;
};

function buildQuery(params: FeedbackJournalListParams): string {
  const sp = new URLSearchParams();
  if (params.limit != null && params.limit > 0) sp.set('limit', String(params.limit));
  if (params.offset != null && params.offset >= 0) sp.set('offset', String(params.offset));
  if (params.organization_id != null && params.organization_id > 0) {
    sp.set('organization_id', String(params.organization_id));
  }
  if (params.category != null) {
    sp.set('category', params.category);
  }
  if (params.is_anonymous === true) {
    sp.set('is_anonymous', 'true');
  }
  if (params.is_anonymous === false) {
    sp.set('is_anonymous', 'false');
  }
  const cf = (params.created_from ?? '').trim();
  const ct = (params.created_to ?? '').trim();
  if (cf) sp.set('created_from', cf);
  if (ct) sp.set('created_to', ct);
  const s = sp.toString();
  return s ? `?${s}` : '';
}

function buildSuperadminFeedbackQuery(params: SuperadminFeedbackJournalParams): string {
  const sp = new URLSearchParams();
  if (params.limit != null && params.limit > 0) sp.set('limit', String(params.limit));
  if (params.offset != null && params.offset >= 0) sp.set('offset', String(params.offset));
  if (params.recipient != null) {
    sp.set('recipient', params.recipient);
  }
  if (params.platformInbox) {
    sp.set('platform_inbox', '1');
  }
  if (params.organization_id != null && params.organization_id > 0) {
    sp.set('organization_id', String(params.organization_id));
  }
  if (params.category != null) {
    sp.set('category', params.category);
  }
  if (params.is_anonymous === true) {
    sp.set('is_anonymous', 'true');
  }
  if (params.is_anonymous === false) {
    sp.set('is_anonymous', 'false');
  }
  if (params.status) sp.set('status', params.status);
  if (params.no_reply) sp.set('no_reply', '1');
  if (params.new_org_comment) sp.set('new_org_comment', '1');
  const cf = (params.created_from ?? '').trim();
  const ct = (params.created_to ?? '').trim();
  if (cf) sp.set('created_from', cf);
  if (ct) sp.set('created_to', ct);
  const s = sp.toString();
  return s ? `?${s}` : '';
}

function normalizeList(raw: unknown): FeedbackJournalListResponse {
  const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const items = Array.isArray(obj.items) ? (obj.items as FeedbackJournalItem[]) : [];
  const total = typeof obj.total === 'number' && Number.isFinite(obj.total) ? obj.total : items.length;
  const countersRaw = obj.counters && typeof obj.counters === 'object' ? (obj.counters as Record<string, unknown>) : null;
  return {
    items,
    total,
    counters: countersRaw
      ? { no_reply: Number(countersRaw.no_reply) || 0, new_org_comment: Number(countersRaw.new_org_comment) || 0 }
      : undefined
  };
}

/** GET `/superadmin/feedback`: «мои» строки или инбокс платформы (`platformInbox`), см. query на сервере. */
export async function fetchSuperadminFeedbackJournal(
  client: ApiClient,
  params: SuperadminFeedbackJournalParams = {}
): Promise<FeedbackJournalListResponse> {
  const q = buildSuperadminFeedbackQuery({
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
    recipient: params.recipient,
    platformInbox: params.platformInbox,
    organization_id: params.organization_id,
    category: params.category,
    is_anonymous: params.is_anonymous,
    created_from: params.created_from,
    created_to: params.created_to,
    status: params.status,
    no_reply: params.no_reply,
    new_org_comment: params.new_org_comment
  });
  const raw = await client.request<unknown>(`/superadmin/feedback${q}`, { method: 'GET' });
  return normalizeList(raw);
}

/** Журнал в контексте организации: только свои обращения (фильтр на сервере). */
export async function fetchAdminFeedbackJournal(
  client: ApiClient,
  params: FeedbackJournalListParams = {}
): Promise<FeedbackJournalListResponse> {
  const q = buildQuery({
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
    organization_id: params.organization_id,
    category: params.category,
    is_anonymous: params.is_anonymous,
    created_from: params.created_from,
    created_to: params.created_to
  });
  const raw = await client.request<unknown>(`/admin/feedback${q}`, { method: 'GET' });
  return normalizeList(raw);
}

/** Поля клиентского портала (GET /portal/tickets и /superadmin/feedback). */
export type PortalTicketItem = FeedbackJournalItem & {
  title?: string;
  status?: string;
  status_label?: string;
  updated_at?: string;
  visible_to_org_admin?: boolean;
  ai_restatement?: string;
  screenshot_media_key?: string;
  screenshot_url?: string;
  last_platform_reply_at?: string;
  last_org_comment_at?: string;
  comments_count?: number;
  unread?: boolean;
  has_new_org_comment?: boolean;
  bug_context?: Record<string, unknown> | null;
  author?: { id?: number; full_name?: string; email?: string; role?: string } | null;
  comments?: PortalTicketComment[];
};

export type PortalTicketComment = {
  id: number;
  entry_id: number;
  side: 'platform' | 'org';
  body: string;
  author_user_id?: number | null;
  author_full_name?: string | null;
  author_email?: string | null;
  status_from?: string;
  status_to?: string;
  created_at: string;
  is_internal?: boolean;
  author_display?: string;
  api_token_id?: number | null;
  api_token?: {
    id: number;
    label: string;
    issued_by_full_name?: string;
    issued_by_email?: string;
  };
};

export type PortalTicketsResponse = {
  items: PortalTicketItem[];
  total: number;
  counters?: { no_reply: number; new_org_comment: number };
};

export type PortalTicketParams = {
  limit?: number;
  offset?: number;
  status?: string;
  category?: string;
  organization_id?: number | null;
};

function portalQuery(params: PortalTicketParams): string {
  const sp = new URLSearchParams();
  if (params.limit != null && params.limit > 0) sp.set('limit', String(params.limit));
  if (params.offset != null && params.offset >= 0) sp.set('offset', String(params.offset));
  if (params.status) sp.set('status', params.status);
  if (params.category) sp.set('category', params.category);
  if (params.organization_id != null && params.organization_id > 0) {
    sp.set('organization_id', String(params.organization_id));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

function normalizeTickets(raw: unknown): PortalTicketsResponse {
  const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const items = Array.isArray(obj.items) ? (obj.items as PortalTicketItem[]) : [];
  const total = typeof obj.total === 'number' && Number.isFinite(obj.total) ? obj.total : items.length;
  const countersRaw = obj.counters && typeof obj.counters === 'object' ? (obj.counters as Record<string, unknown>) : null;
  return {
    items,
    total,
    counters: countersRaw
      ? {
          no_reply: Number(countersRaw.no_reply) || 0,
          new_org_comment: Number(countersRaw.new_org_comment) || 0
        }
      : undefined
  };
}

/** GET `/portal/tickets` — сторона организации. */
export async function fetchPortalTickets(
  client: ApiClient,
  params: PortalTicketParams = {},
  impersonateOrganizationId?: number | null
): Promise<PortalTicketsResponse> {
  const raw = await client.request<unknown>(`/portal/tickets${portalQuery(params)}`, {
    method: 'GET',
    impersonateOrganizationId
  });
  return normalizeTickets(raw);
}

/** GET `/portal/tickets/{id}` — деталь и тред, отмечает прочтение. */
export async function fetchPortalTicket(
  client: ApiClient,
  id: number,
  params: { organization_id?: number | null } = {},
  impersonateOrganizationId?: number | null
): Promise<PortalTicketItem> {
  return client.request<PortalTicketItem>(`/portal/tickets/${id}${portalQuery(params)}`, {
    method: 'GET',
    impersonateOrganizationId
  });
}

/** POST `/portal/tickets/{id}/comments` — комментарий организации. */
export async function postPortalTicketComment(
  client: ApiClient,
  id: number,
  body: string,
  params: { organization_id?: number | null } = {},
  impersonateOrganizationId?: number | null
): Promise<PortalTicketComment> {
  return client.request<PortalTicketComment>(`/portal/tickets/${id}/comments${portalQuery(params)}`, {
    method: 'POST',
    body: { body },
    impersonateOrganizationId
  });
}

/** GET `/portal/unread-count` — бейдж непрочитанного в меню. */
export async function fetchPortalUnreadCount(
  client: ApiClient,
  impersonateOrganizationId?: number | null
): Promise<number> {
  const raw = await client.request<{ count?: number }>('/portal/unread-count', {
    method: 'GET',
    impersonateOrganizationId
  });
  return Number(raw?.count) || 0;
}

/** GET `/superadmin/feedback/{id}` — деталь для платформы. */
export async function fetchSuperadminFeedbackDetail(client: ApiClient, id: number): Promise<PortalTicketItem> {
  return client.request<PortalTicketItem>(`/superadmin/feedback/${id}`, { method: 'GET' });
}

/** POST `/superadmin/feedback/{id}/comments` — ответ платформы и смена статуса. */
export async function postSuperadminFeedbackComment(
  client: ApiClient,
  id: number,
  payload: { body: string; status?: string; internal?: boolean }
): Promise<PortalTicketComment> {
  return client.request<PortalTicketComment>(`/superadmin/feedback/${id}/comments`, {
    method: 'POST',
    body: payload
  });
}
